import { html, LitElement } from 'lit';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import { ApplicantsService } from '../../../services/applicants.service.js';
import { DebugService } from '../../../shared/services/debug.service.js';
import { SessionService } from '../../../core/services/session/session.service.js';
import { REFI_PERSONAL_CONST } from '../../../shared/constants/app.constants.js';
import { stratum } from '../../../../constants/stratum.js';
import { ApplicationService } from '../../../services/application.service';

class RefiIncomeReview extends LitElement {
  constructor() {
    super();
    this.primaryAppType = REFI_PERSONAL_CONST.APPLICANT_TYPE.PRIMARY_APPLICANT;
    this.coAppType = REFI_PERSONAL_CONST.APPLICANT_TYPE.CO_APPLICANT;
    this.isPrimary = true;
    this.cardId = 'primary-income-card';
    this.debug = new DebugService('RefiIncomeReview');
  }

  static properties = {
    applicantType: { type: String, attribute: 'applicant-type' },
    jobTitle: { type: String },
    employmentStatus: { type: String },
    income: { type: String },
    experiments: { type: Object, reflect: true },
    v2Routing: {
      type: Boolean,
      attribute: 'v2-routing',
      reflect: true,
      hasChanged: (newVal) => {
        ApplicationService.isV2Routing = newVal;
      },
    },
  };

  async connectedCallback() {
    super.connectedCallback();
    const eAppId = SessionService.session?.eAppId;

    try {
      await ApplicantsService.getApplicantInformation(eAppId).then(
        (response) => {
          // Set isPrimary and cardId based on applicantType
          this.isPrimary = this.applicantType === this.primaryAppType;
          this.cardId = this.isPrimary ? this.cardId : 'co-income-card';

          const applicantSearchString = this.isPrimary
            ? 'Applicant'
            : 'CoApplicant';
          const applicant = response.applicants.find(
            (applicant) => applicant.applicantType === applicantSearchString
          );

          if (
            !applicant.employment.employmentType ||
            !applicant.employment.incomeAmount
          ) {
            this.debug.log('Missing applicant income field');
            stratum.publishTag(TagKey.PERSONAL_CHECKOUT_PERSONAL_MFE_LOAD, {
              replacements: {
                value: 'income mfe - failed',
              },
            });
            this.dispatchFailedToLoadOrUpdateEvent();
          } else {
            stratum.publishTag(TagKey.PERSONAL_CHECKOUT_PERSONAL_MFE_LOAD, {
              replacements: {
                value: 'income mfe - successful',
              },
            });
          }

          this.jobTitle = applicant.employment.jobTitle;
          this.employmentStatus = applicant.employment.employmentType;
          this.income = applicant.employment.incomeAmount;

          this.employedOrSelfEmployed =
            this.employmentStatus &&
            (this.employmentStatus.toLowerCase() ===
              REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.EMPLOYED ||
              this.employmentStatus.toLowerCase() ===
                REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.SELF_EMPLOYED);
          this.dispatchCompleteLoadingEvent();
        }
      );

      try {
        await ApplicantsService.getStips(eAppId).then((res) => {
          const incomeStip = res.find(
            (stip) =>
              stip.taskType === REFI_PERSONAL_CONST.STIP_TYPE.PROOF_OF_INCOME
          );
          if (!incomeStip) {
            this.debug.log(
              'Missing Proof of Income stip info after call to Stips API'
            );
          }
          if (
            incomeStip.taskStatus.toString().toUpperCase() ===
            REFI_PERSONAL_CONST.STIP_INCOMPLETE
          ) {
            this.dispatchEvent(
              new CustomEvent('checkout-update-mudflap', {
                bubbles: true,
                composed: true,
                detail: {
                  actionName: 'update-mudflap',
                  id: this.cardId,
                  text: 'You’ll upload income verification later',
                },
              })
            );
          }
          stratum.publishTag(TagKey.INCOME_CHECKOUT_POI_STIP_MUDFLAP_LOADS, {
            replacements: {
              value: `successful - ${incomeStip.taskStatus}`,
            },
          });
        });
      } catch (err) {
        stratum.publishTag(TagKey.INCOME_CHECKOUT_POI_STIP_MUDFLAP_LOADS, {
          replacements: {
            value: 'failed',
          },
        });
        this.debug.log(
          'Failed to retrieve stip information for income review on checkout: ',
          err.message
        );
      }
    } catch (err) {
      stratum.publishTag(TagKey.PERSONAL_CHECKOUT_PERSONAL_MFE_LOAD, {
        replacements: {
          value: 'income mfe - failed',
        },
      });
      this.debug.log(
        'Failed to retrieve income information for review on checkout: ',
        err.message
      );
      this.dispatchFailedToLoadOrUpdateEvent();
    }
  }

  dispatchCompleteLoadingEvent() {
    this.dispatchEvent(
      new CustomEvent('checkout-completed-loading', {
        bubbles: true,
        composed: true,
        detail: {
          id: this.cardId,
        },
      })
    );
    this.debug.log('completed loading event fired');
  }

  dispatchFailedToLoadOrUpdateEvent() {
    this.dispatchEvent(
      new CustomEvent('failed-load-or-update', {
        bubbles: true,
        composed: true,
        detail: {
          id: this.cardId,
        },
      })
    );
    this.checkout = 'error';
    this.debug.log('failed to load or update event fired');
  }

  render() {
    return this.employmentStatus && this.income
      ? html` <div>
          <fs-body-text variant="primary" id="job-title"
            ><span data-qm-encrypt
              >${this.jobTitle &&
              this.jobTitle !== '' &&
              this.employedOrSelfEmployed
                ? this.jobTitle
                : null}</span
            ></fs-body-text
          >
          <fs-body-text variant="primary"
            ><span data-qm-encrypt
              >${this.formatEmploymentStatus(this.employmentStatus)}</span
            ></fs-body-text
          >
          <fs-body-text variant="primary"
            ><span data-qm-encrypt
              >${this.formatIncome(this.income)}</span
            ></fs-body-text
          >
        </div>`
      : null;
  }

  formatEmploymentStatus(employmentStatus) {
    switch (employmentStatus?.toLowerCase()) {
      case REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.SELF_EMPLOYED:
        return 'Self-Employed';
      case REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.EMPLOYED:
        return 'Employed';
      case REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.UNEMPLOYED:
        return 'Unemployed';
      case REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.RETIRED:
        return 'Retired';
      case REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.OTHER:
        return 'Other';
      default:
        this.debug.log(
          'Employment status doesn\'t match any known type. Showed "Other".'
        );
        return 'Other';
    }
  }

  formatIncome(income) {
    const formattedIncome = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(income);
    return formattedIncome === '$0' ? '' : `${formattedIncome}/annually`;
  }
}

customElements.define('refi-income-review', RefiIncomeReview);

// Defining it again so that we can import a separate instance for coapp on checkout page
customElements.define(
  'co-refi-income-review',
  class extends RefiIncomeReview {}
);
